import { useState } from 'react';
import { TabGroup } from 'calc-nav';
import { useStore } from '@/store';
import { Button } from '@/components/ui/Button';
import { SearchInput } from '@/components/ui/SearchInput';
import { BCS_REFERENCE, REGULATORY_REFERENCE } from '@/config/reference';
import { EXAMPLES } from '@/data/examples';

/**
 * Reference screen — worked examples, regulatory quick-reference, BCS table.
 *
 * Uses calc-nav's TabGroup so each reference category gets its own panel.
 * Content stays mounted across tab switches (UX Invariant #8), and the
 * active tab is persisted in the URL hash (#reference/<tab>) for sharing.
 */
export function ReferenceScreen() {
  return (
    <div className="reference-screen">
      <TabGroup
        section="reference"
        stickyTop="var(--topbar-height)"
        tabs={[
          { id: 'examples', label: 'Worked Examples', content: <WorkedExamplesSection /> },
          { id: 'regulatory', label: 'Regulatory', content: <RegulatorySection /> },
          { id: 'bcs', label: 'BCS Classification', content: <BcsSection /> },
        ]}
      />
    </div>
  );
}

function WorkedExamplesSection() {
  const loadExample = useStore((s) => s.loadExample);
  const setActiveScreen = useStore((s) => s.setActiveScreen);
  const [query, setQuery] = useState('');

  const filtered = query
    ? EXAMPLES.filter((ex) =>
        ex.name.toLowerCase().includes(query.toLowerCase()) ||
        ex.description.toLowerCase().includes(query.toLowerCase()))
    : EXAMPLES;

  return (
    <div className="flex-col-gap-3">
      <SearchInput
        value={query}
        onChange={setQuery}
        placeholder="Search examples…"
        ariaLabel="Search worked examples"
      />
      {filtered.length === 0 ? (
        <p className="empty-state">No examples match "{query}".</p>
      ) : (
        <>
        {filtered.map((ex) => (
        <div key={ex.id} className="reference-card">
          <h4>{ex.name}</h4>
          <p className="field-hint">{ex.description}</p>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              loadExample(ex.build());
              setActiveScreen('calculator');
            }}
          >
            Load into Calculator
          </Button>
        </div>
        ))}
        </>
      )}
    </div>
  );
}

function RegulatorySection() {
  const [query, setQuery] = useState('');

  const filtered = query
    ? REGULATORY_REFERENCE.filter((entry) =>
        entry.authority.toLowerCase().includes(query.toLowerCase()) ||
        entry.notes.toLowerCase().includes(query.toLowerCase()))
    : REGULATORY_REFERENCE;

  return (
    <div className="flex-col-gap-3">
      <SearchInput
        value={query}
        onChange={setQuery}
        placeholder="Search regulatory guidance…"
        ariaLabel="Search regulatory guidance"
      />
      {filtered.length === 0 ? (
        <p className="empty-state">No entries match "{query}".</p>
      ) : (
        <div className="table-scroll">
          <table className="generic-table">
            <thead>
              <tr>
                <th>Authority</th>
                <th>Acceptance Window</th>
                <th>Endpoints</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((entry) => (
                <tr key={entry.authority}>
                  <td><strong>{entry.authority}</strong></td>
                  <td>{entry.acceptanceWindow}</td>
                  <td>{entry.endpoints}</td>
                  <td className="field-hint">{entry.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BcsSection() {
  const [query, setQuery] = useState('');

  const filtered = query
    ? BCS_REFERENCE.filter((entry) =>
        entry.name.toLowerCase().includes(query.toLowerCase()) ||
        entry.description.toLowerCase().includes(query.toLowerCase()))
    : BCS_REFERENCE;

  return (
    <div className="flex-col-gap-3">
      <SearchInput
        value={query}
        onChange={setQuery}
        placeholder="Search BCS classes…"
        ariaLabel="Search BCS classification"
      />
      {filtered.length === 0 ? (
        <p className="empty-state">No entries match "{query}".</p>
      ) : (
        <div className="table-scroll">
          <table className="generic-table">
            <thead>
              <tr>
                <th>Class</th>
                <th>Solubility</th>
                <th>Permeability</th>
                <th>Biowaiver</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((entry) => (
                <tr key={entry.cls}>
                  <td><strong>{entry.name}</strong></td>
                  <td>{entry.solubility}</td>
                  <td>{entry.permeability}</td>
                  <td>{entry.biowaiver}</td>
                  <td className="field-hint">{entry.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
